/* ================================================
   Offense Summary — Pure derivation function
   PDF-compatible: no React, no store imports.
   ================================================ */

import type {
  ICharacter,
  IPowerEffect,
  IModifierDef,
  ISkillDef,
  IAdvantageDef,
  ICharacterPowerComponent,
  IResource,
} from '../../entities/types';
import { getEffectiveAbilityRank, isStrengthBasedDamage } from './abilityRanks';
import { effectiveTraitCharacter, circumstanceBonus } from './traitValues';
import { resolveEffectiveRange } from './effectParameters';
import { getAffectedRanks, getCharacterStrength, getComponentEffectRanks, getRankBoundaries } from './componentRanks';
import { getResourceCharacter, getResourceAttackBonus } from './resourceContext';
import { resolveModifierDefinition } from './rulesCatalog';

/**
 * A single row in the Offense panel table.
 * isAE: true = this row is an Alternate Effect sub-row (indented, dimmed).
 * isManual: true = user-created custom attack row.
 * isNoRoll: true = perception/area range (no attack check, effect capped at PL).
 */
export interface IOffenseEntry {
  id: string;               // unique key for React rendering
  name: string;             // display name
  bonus: string;            // attack bonus string, e.g. "+8" or "—" for no-roll
  bonusValue: number | null;// numeric bonus for PL validation; null = no-roll
  bonusBreakdown: string;   // tooltip: "FGT 4 + Accurate 2 = +6"
  range: string;            // "close" | "ranged" | "perception" | "personal"
  effect: string;           // e.g. "Damage 12" or "Affliction 8"
  notes: string;            // extra info: Crit 19-20, Multiattack, etc.
  isAE: boolean;            // true = indented AE sub-row
  isManual: boolean;        // true = custom attack row from IManualOffenseRow
  isNoRoll: boolean;        // true = perception/area range, no attack check
  parentId?: string;        // parent power id when isAE = true
  /** Mechanical classification for the Targeted Effects view. */
  interaction: 'attack' | 'resistance' | 'targeted';
  requiresAttackCheck: boolean;
  causesResistance: boolean;
  tags: Array<'attack' | 'resistance' | 'area' | 'perception' | 'affects-others' | 'dynamic'>;
  sourceType: 'power' | 'equipment' | 'resource' | 'manual' | 'unarmed';
  sourceName?: string;
  componentName?: string;
  componentId?: string;
  relationship: 'base' | 'alternate' | 'dynamic-alternate' | 'manual' | 'unarmed';
  resistance?: string;
  effectRank: number | null;
}

export function parseEffectRank(effect: string): number | null {
  const match = effect.match(/\b(?:damage|dano|affliction|aflição|nullify|nulificar|weaken|enfraquecer)\s+(-?\d+)/i)
    ?? effect.match(/\b(\d+)\b/);
  return match ? Number(match[1]) : null;
}

function manualResistanceLabel(effect: string): string | undefined {
  const rank = parseEffectRank(effect);
  if (rank === null) return undefined;
  if (/\b(?:nullify|nulificar)\s+-?\d+/i.test(effect)) return nullifyResistanceLabel(rank, 'Will');
  return `Resistance DC ${(/\b(?:damage|dano)\s+-?\d+/i.test(effect) ? 15 : 10) + rank}`;
}

function nullifyResistanceLabel(rank: number, resistance: string): string {
  return `Nullify ${rank} vs max(effect rank, ${resistance}); subject: effect rank`;
}

function hasModifier(comp: ICharacterPowerComponent, modifierId: string): boolean {
  return comp.modifiers.some((modifier) => modifier.modifierId === modifierId);
}

/** Check if a component has the Area extra (any variant) — makes it no-roll. */
function hasAreaExtra(
  comp: ICharacterPowerComponent,
  modifierDefs: IModifierDef[],
  effect?: IPowerEffect,
): boolean {
  return comp.modifiers.some((m) => {
    const def = effect ? resolveModifierDefinition(m, effect, modifierDefs).definition : modifierDefs.find((d) => d.id === m.modifierId);
    return def?.id === 'area' || def?.id.startsWith('area_') === true || def?.id.endsWith('_area') === true;
  });
}

/** Check if a component has the Affect Others extra. */
function hasAffectOthers(
  comp: ICharacterPowerComponent,
  modifierDefs: IModifierDef[]
): boolean {
  return comp.modifiers.some((m) => {
    const def = modifierDefs.find((d) => d.id === m.modifierId);
    return def?.id === 'affects_others';
  });
}

/** The Attack extra makes a non-attack effect require an attack check. */
function hasAttackExtra(comp: ICharacterPowerComponent): boolean {
  return hasModifier(comp, 'attack');
}

/** Power-specific Resistible flaws use ids such as resistible_illusion. */
function hasResistibleModifier(comp: ICharacterPowerComponent): boolean {
  return comp.modifiers.some((modifier) => modifier.modifierId === 'resistible' || modifier.modifierId.startsWith('resistible_'));
}

/** Ranks of 'Accurate' extra on a component (+2 to attack per rank). */
function getAccurateBonus(
  comp: ICharacterPowerComponent,
  modifierDefs: IModifierDef[]
): number {
  const acc = comp.modifiers.filter((m) => {
    const def = modifierDefs.find((d) => d.id === m.modifierId);
    return def?.id === 'accurate';
  });
  const inaccurate = comp.modifiers.filter((m) => {
    const def = modifierDefs.find((d) => d.id === m.modifierId);
    return def?.id === 'inaccurate';
  });
  return acc.reduce((sum, modifier) => sum + modifier.ranks * 2, 0) - inaccurate.reduce((sum, modifier) => sum + modifier.ranks * 2, 0);
}

function getInaccuratePenalty(
  comp: ICharacterPowerComponent,
  modifierDefs: IModifierDef[]
): number {
  const inaccurate = comp.modifiers.filter((m) => {
    const def = modifierDefs.find((d) => d.id === m.modifierId);
    return def?.id === 'inaccurate';
  });
  return inaccurate.reduce((sum, modifier) => sum + modifier.ranks * 2, 0);
}

/**
 * F-12: Derive attack bonus with full breakdown for a given component.
 *
 * Rules (per M&M 3e):
 * - Close attack:  FGT + Close Attack advantage + matching Close Combat skill + Accurate ×2
 * - Ranged attack: DEX + Ranged Attack advantage + matching Ranged Combat skill + Accurate ×2
 * - Perception or Area range: no attack roll (returns null + isNoRoll = true)
 * - Personal range: no attack roll (returns null + isNoRoll = true)
 */
export function calcAttackBonus(
  effectRange: string,
  powerName: string,
  component: ICharacterPowerComponent,
  character: ICharacter,
  skillDefs: ISkillDef[],
  modifierDefs: IModifierDef[],
  effectDef?: IPowerEffect,
): { value: number | null; breakdown: string; isNoRoll: boolean } {
  const { abilities, absentAbilities, skills, advantages } = character;
  const attackSkill = typeof component.fieldValues?.attackSkill === 'string' ? component.fieldValues.attackSkill : powerName;

  // Calculate effective range (accounting for Increased Range modifier)
  const effectiveRange = resolveEffectiveRange(
    effectRange as IPowerEffect['range'],
    component,
    effectDef ? { effect: effectDef, modifierDefs } : undefined,
  ).value;

  // ── Perception or personal range → no attack check ──
  if (effectiveRange === 'perception' || effectiveRange === 'personal') {
    return { value: null, breakdown: '—', isNoRoll: true };
  }

  // ── Area extras → no attack check (auto-hit) ──
  if (hasAreaExtra(component, modifierDefs, effectDef)) {
    return { value: null, breakdown: 'Area (auto)', isNoRoll: true };
  }

  const accurate = getAccurateBonus(component, modifierDefs);
  const inaccuratePenalty = getInaccuratePenalty(component, modifierDefs);
  const accurateBonus = accurate + inaccuratePenalty;
  const parts: string[] = [];

  if (effectiveRange === 'close') {
    const base = getEffectiveAbilityRank(abilities, absentAbilities, 'fgt');
    parts.push(`FGT ${base}`);

    // Close Attack advantage (generic all-close bonus)
    const closeAttackAdv = advantages.find((a) => a.advantageId === 'close_attack');
    const closeAdvRanks = closeAttackAdv?.ranks ?? 0;
    if (closeAdvRanks > 0) parts.push(`Close Atk ${closeAdvRanks}`);

    // Close Combat skill with matching subtype
    const skillEntry = skills.find((s) => {
      const def = skillDefs.find((d) => d.id === s.skillId);
      return def?.id === 'close_combat' && s.subtype?.toLowerCase() === attackSkill.toLowerCase();
    });
    const skillRanks = (skillEntry?.ranks ?? 0) + (skillEntry?.otherBonus ?? 0) + (skillEntry ? circumstanceBonus(character, { kind: 'skill', skillId: skillEntry.skillId, subtype: skillEntry.subtype }) : 0);
    if (skillRanks !== 0) parts.push(`Close Combat: ${attackSkill} ${skillRanks}`);

    if (accurateBonus > 0) parts.push(`Accurate ${accurateBonus}`);
    if (inaccuratePenalty > 0) parts.push(`Inaccurate -${inaccuratePenalty}`);

    const total = base + closeAdvRanks + skillRanks + accurate;
    return { value: total, breakdown: parts.join(' + '), isNoRoll: false };
  }

  // ── Ranged ──────────────────────────────────────────────────────
  const base = getEffectiveAbilityRank(abilities, absentAbilities, 'dex');
  parts.push(`DEX ${base}`);

  const rangedAttackAdv = advantages.find((a) => a.advantageId === 'ranged_attack');
  const rangedAdvRanks = rangedAttackAdv?.ranks ?? 0;
  if (rangedAdvRanks > 0) parts.push(`Ranged Atk ${rangedAdvRanks}`);

  const skillEntry = skills.find((s) => {
    const def = skillDefs.find((d) => d.id === s.skillId);
    return def?.id === 'ranged_combat' && s.subtype?.toLowerCase() === attackSkill.toLowerCase();
  });
  const skillRanks = (skillEntry?.ranks ?? 0) + (skillEntry?.otherBonus ?? 0) + (skillEntry ? circumstanceBonus(character, { kind: 'skill', skillId: skillEntry.skillId, subtype: skillEntry.subtype }) : 0);
  if (skillRanks !== 0) parts.push(`Ranged Combat: ${attackSkill} ${skillRanks}`);

  if (accurateBonus > 0) parts.push(`Accurate ${accurateBonus}`);
  if (inaccuratePenalty > 0) parts.push(`Inaccurate -${inaccuratePenalty}`);

  const total = base + rangedAdvRanks + skillRanks + accurate;
  return { value: total, breakdown: parts.join(' + '), isNoRoll: false };
}

/** Extract notable modifier notes from a component (Crit, Multiattack, etc.). */
function extractNotes(comp: ICharacterPowerComponent): string {
  const flags: string[] = [];
  for (const mod of comp.modifiers) {
    if (mod.modifierId === 'multiattack')       flags.push('Multiattack');
    if (mod.modifierId === 'improved_critical')  flags.push('Crit 19–20');
    if (mod.modifierId === 'penetrating')        flags.push(`Penetrating ${mod.ranks}`);
  }
  return flags.join(', ');
}

type SourceType = 'power' | 'equipment' | 'resource';

function getResistanceLabel(
  def: IPowerEffect,
  comp: ICharacterPowerComponent,
  effectRank: number
): string | undefined {
  const configured = comp.fieldValues?.resistance;
  const configuredValue = typeof configured === 'string' ? configured : undefined;
  const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
  const alternate = comp.modifiers.find((modifier) => modifier.modifierId === 'alternate_resistance');
  const resistance = typeof alternate?.options?.subtypeId === 'string' && alternate.options.subtypeId
    ? alternate.options.subtypeId
    : configuredValue;
  if (def.id === 'nullify') return nullifyResistanceLabel(effectRank, capitalize(resistance ?? (hasModifier(comp, 'alternate_resistance') ? 'fortitude' : 'will')));
  if (resistance) return `${capitalize(resistance)} DC ${(def.id === 'damage' ? 15 : 10) + effectRank}`;

  if (def.id === 'damage') return `Toughness DC ${15 + effectRank}`;
  if (hasResistibleModifier(comp) || def.type === 'attack' || hasAttackExtra(comp)) return `Resistance DC ${10 + effectRank}`;
  return undefined;
}

function getInteraction(
  def: IPowerEffect,
  comp: ICharacterPowerComponent,
  modifierDefs: IModifierDef[]
): { interaction: IOffenseEntry['interaction']; requiresAttackCheck: boolean; causesResistance: boolean; isNoRoll: boolean } | null {
  const hasAttack = def.type === 'attack' || hasAttackExtra(comp);
  const hasArea = hasAreaExtra(comp, modifierDefs, def);
  const effectiveRange = resolveEffectiveRange(def.range, comp, { effect: def, modifierDefs }).value;
  const causesResistance = hasAttack || hasResistibleModifier(comp);

  if (hasAttack) {
    const isNoRoll = hasArea || effectiveRange === 'perception';
    return {
      interaction: isNoRoll ? 'resistance' : 'attack',
      requiresAttackCheck: !isNoRoll,
      causesResistance: true,
      isNoRoll,
    };
  }

  if (causesResistance) {
    return { interaction: 'resistance', requiresAttackCheck: false, causesResistance: true, isNoRoll: true };
  }

  if (def.id === 'healing' && (effectiveRange !== 'close' || hasArea)) {
    const requiresAttackCheck = effectiveRange === 'ranged' && !hasArea;
    return { interaction: 'targeted', requiresAttackCheck, causesResistance: false, isNoRoll: !requiresAttackCheck };
  }

  if (hasAffectOthers(comp, modifierDefs)) {
    return { interaction: 'targeted', requiresAttackCheck: false, causesResistance: false, isNoRoll: true };
  }

  return null;
}

function getTags(
  interaction: IOffenseEntry['interaction'],
  comp: ICharacterPowerComponent,
  effectiveRange: string,
  modifierDefs: IModifierDef[],
  dynamic: boolean,
  def: IPowerEffect,
): IOffenseEntry['tags'] {
  const tags: IOffenseEntry['tags'] = [];
  if (interaction === 'attack') tags.push('attack');
  if (interaction === 'resistance') tags.push('resistance');
  if (hasAreaExtra(comp, modifierDefs, def)) tags.push('area');
  if (effectiveRange === 'perception') tags.push('perception');
  if (hasAffectOthers(comp, modifierDefs)) tags.push('affects-others');
  if (dynamic) tags.push('dynamic');
  return tags;
}

function createComponentProfile(
  character: ICharacter,
  def: IPowerEffect,
  comp: ICharacterPowerComponent,
  skillDefs: ISkillDef[],
  modifierDefs: IModifierDef[],
  source: { id: string; name: string; type: SourceType; parentId?: string },
  relationship: Extract<IOffenseEntry['relationship'], 'base' | 'alternate' | 'dynamic-alternate'>,
  alternateName?: string
): IOffenseEntry | null {
  const interaction = getInteraction(def, comp, modifierDefs);
  if (!interaction) return null;

  const effectiveRange = resolveEffectiveRange(def.range, comp, { effect: def, modifierDefs }).value;
  const strengthContribution = isStrengthBasedDamage(comp)
    ? getEffectiveAbilityRank(character.abilities, character.absentAbilities, 'str')
    : 0;
  const effectRank = comp.ranks + strengthContribution;
  const profileName = alternateName || source.name || def.name;
  const bonus = interaction.requiresAttackCheck
    ? calcAttackBonus(def.range, profileName, comp, character, skillDefs, modifierDefs, def)
    : { value: null, breakdown: interaction.causesResistance ? 'No attack check' : 'Targeted effect', isNoRoll: true };

  return {
    id: `${source.type}:${source.id}:${relationship}:${comp.id}`,
    name: profileName,
    bonus: bonus.value === null ? '—' : `${bonus.value >= 0 ? '+' : ''}${bonus.value}`,
    bonusValue: bonus.value,
    bonusBreakdown: bonus.breakdown,
    range: effectiveRange,
    effect: `${def.name} ${effectRank}`,
    notes: extractNotes(comp),
    isAE: relationship !== 'base',
    isManual: false,
    isNoRoll: interaction.isNoRoll,
    parentId: relationship === 'base' ? undefined : source.parentId ?? source.id,
    interaction: interaction.interaction,
    requiresAttackCheck: interaction.requiresAttackCheck,
    causesResistance: interaction.causesResistance,
    tags: getTags(interaction.interaction, comp, effectiveRange, modifierDefs, relationship === 'dynamic-alternate', def),
    sourceType: source.type,
    sourceName: source.name || def.name,
    componentName: def.name,
    componentId: comp.id,
    relationship,
    resistance: interaction.causesResistance ? getResistanceLabel(def, comp, effectRank) : undefined,
    effectRank,
  };
}

function createComponentProfiles(
  character: ICharacter,
  def: IPowerEffect,
  component: ICharacterPowerComponent,
  skillDefs: ISkillDef[],
  modifierDefs: IModifierDef[],
  source: { id: string; name: string; type: SourceType; parentId?: string },
  relationship: Extract<IOffenseEntry['relationship'], 'base' | 'alternate' | 'dynamic-alternate'>,
  alternateName?: string,
): IOffenseEntry[] {
  const ranks = getComponentEffectRanks(component, getCharacterStrength(character));
  const profiles = new Map<string, IOffenseEntry>();
  for (const boundary of getRankBoundaries(component.modifiers, ranks)) {
    const slice: ICharacterPowerComponent = {
      ...component,
      ranks: boundary,
      fieldValues: { ...component.fieldValues, damageBasis: 'standalone' },
      modifiers: component.modifiers.filter((modifier) =>
        getAffectedRanks(modifier) === undefined || boundary <= getAffectedRanks(modifier)!),
    };
    const profile = createComponentProfile(character, def, slice, skillDefs, modifierDefs, source, relationship, alternateName);
    if (!profile) continue;
    // Keep the highest cumulative rank for each distinct mechanical use.
    // Area 4 + Damage 12 yields Area 4 and a direct Damage 12 attack.
    const key = JSON.stringify([profile.range, profile.tags, profile.bonusValue, profile.resistance?.replace(/DC -?\d+$/, ''), profile.notes]);
    profiles.set(key, { ...profile, id: `${profile.id}:${boundary}` });
  }
  return [...profiles.values()];
}

/**
 * Builds factual target-effect profiles from every source that can be applied
 * to another character. It deliberately does not infer an "offensive" or
 * "support" intent: filters are based only on roll, resistance and modifiers.
 */
export function buildTargetedEffectProfiles(
  character: ICharacter,
  powerDefs: IPowerEffect[],
  skillDefs: ISkillDef[],
  _advantageDefs: IAdvantageDef[],
  modifierDefs: IModifierDef[] = [],
  translations?: { unarmed: string; damage: string },
  resources: IResource[] = [],
  includeCircumstances = true,
): IOffenseEntry[] {
  character = effectiveTraitCharacter(character, resources);
  if (!includeCircumstances) character = { ...character, traitModifiers: undefined };
  const entries: IOffenseEntry[] = [];
  const { abilities, absentAbilities, skills } = character;

  // ── 1. Unarmed attack (always first) ───────────────────────────
  {
    const base = getEffectiveAbilityRank(abilities, absentAbilities, 'fgt');
    const strength = getEffectiveAbilityRank(abilities, absentAbilities, 'str');
    const closeAttackAdv = character.advantages.find((a) => a.advantageId === 'close_attack');
    const closeAdvRanks = closeAttackAdv?.ranks ?? 0;

    const unarmedSkill = skills.find((s) => {
      const def = skillDefs.find((d) => d.id === s.skillId);
      return def?.id === 'close_combat' && s.subtype?.toLowerCase() === 'unarmed';
    });
    const unarmedSkillRanks = (unarmedSkill?.ranks ?? 0) + (unarmedSkill?.otherBonus ?? 0) + (unarmedSkill ? circumstanceBonus(character, { kind: 'skill', skillId: unarmedSkill.skillId, subtype: unarmedSkill.subtype }) : 0);

    const total = base + closeAdvRanks + unarmedSkillRanks;
    const parts = [`FGT ${base}`];
    if (closeAdvRanks > 0) parts.push(`Close Atk ${closeAdvRanks}`);
    if (unarmedSkillRanks !== 0) parts.push(`Unarmed ${unarmedSkillRanks}`);

    entries.push({
      id: '__unarmed__',
      name: translations?.unarmed ?? 'Unarmed',
      bonus: `${total >= 0 ? '+' : ''}${total}`,
      bonusValue: total,
      bonusBreakdown: parts.join(' + '),
      range: 'close',
      effect: `${translations?.damage ?? 'Damage'} ${strength}`,
      notes: '',
      isAE: false,
      isManual: false,
      isNoRoll: false,
      interaction: 'attack',
      requiresAttackCheck: true,
      causesResistance: true,
      tags: ['attack', 'resistance'],
      sourceType: 'unarmed',
      sourceName: translations?.unarmed ?? 'Unarmed',
      componentName: translations?.damage ?? 'Damage',
      relationship: 'unarmed',
      resistance: `Toughness DC ${15 + strength}`,
      effectRank: strength,
    });
  }

  const appendSourceProfiles = (sourcePowers: ICharacter['powers'], sourceType: SourceType, context = character, resource?: IResource) => {
    for (const sourcePower of sourcePowers) {
      const start = entries.length;
      const source = { id: resource ? `${resource.id}:${sourcePower.id}` : sourcePower.id, name: sourcePower.name, type: sourceType };
      for (const comp of sourcePower.components) {
        const def = powerDefs.find((effect) => effect.id === comp.effectId);
        if (!def) continue;
        entries.push(...createComponentProfiles(context, def, comp, skillDefs, modifierDefs, source, 'base'));
      }
      for (const ae of sourcePower.alternateEffects ?? []) {
        const relationship = ae.dynamic ? 'dynamic-alternate' : 'alternate';
        for (const comp of ae.components) {
          const def = powerDefs.find((effect) => effect.id === comp.effectId);
          if (!def) continue;
          const alternateSource = { ...source, id: `${source.id}:${ae.id}`, parentId: source.id };
          entries.push(...createComponentProfiles(context, def, comp, skillDefs, modifierDefs, alternateSource, relationship, ae.name));
        }
      }
      if (resource) {
        const fixedBonus = getResourceAttackBonus(resource, sourcePower);
        for (let index = start; index < entries.length; index++) {
          const entry = entries[index];
          entries[index] = { ...entry, name: [resource.name, entry.name].filter(Boolean).join(' — '), sourceName: resource.name || entry.sourceName,
            ...(fixedBonus !== undefined && entry.requiresAttackCheck ? { bonusValue: fixedBonus, bonus: `+${fixedBonus}`, bonusBreakdown: `PL ${fixedBonus}` } : {}) };
        }
      }
    }
  };

  appendSourceProfiles(character.powers, 'power');
  appendSourceProfiles(character.equipment ?? [], 'equipment');
  for (const link of character.resourceLinks ?? []) {
    const resource = resources.find((item) => item.id === link.resourceId);
    if (!resource) continue;
    const powers = resource.type === 'vehicle'
      ? resource.systems
      : resource.type === 'headquarters'
        ? resource.effects
        : [resource.power];
    appendSourceProfiles(powers, 'resource', getResourceCharacter(character, resource), resource);
  }

  // ── 3. Manual offense rows (F-13) ──────────────────────────────
  for (const row of character.manualOffenseRows ?? []) {
    entries.push({
      id: row.id,
      name: row.name,
      bonus: row.bonus >= 0 ? `+${row.bonus}` : `${row.bonus}`,
      bonusValue: row.bonus,
      bonusBreakdown: 'Custom',
      range: row.range,
      effect: row.effect,
      notes: row.notes,
      isAE: false,
      isManual: true,
      isNoRoll: row.range === 'perception',
      interaction: row.range === 'perception' ? 'resistance' : 'attack',
      requiresAttackCheck: row.range !== 'perception',
      causesResistance: true,
      tags: row.range === 'perception' ? ['resistance', 'perception'] : ['attack', 'resistance'],
      sourceType: 'manual',
      sourceName: row.name,
      componentName: row.name,
      relationship: 'manual',
      resistance: manualResistanceLabel(row.effect),
      effectRank: parseEffectRank(row.effect),
    });
  }

  return entries;
}

/** Legacy export kept so existing PDF callers remain compatible. */
export const buildOffenseSummary = buildTargetedEffectProfiles;
